// TestResultScreen.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Award, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import ErrorBox from 'components/ErrorBox';
import { getResult } from 'api/apiHelper';

interface QuestionResult {
  question_order: number;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  selected_option: string | null;
  correct_option: string;
  is_correct: boolean | null;
}

interface TestResult {
  applicant_name: string;
  job_title: string;
  status: string;
  started_at: string;
  completed_at: string;
  total_questions: number;
  questions_attempted: number;
  correct_answers: number;
  score_percentage: number;
  test_questions_with_answers: QuestionResult[];
}

const TestResultScreen: React.FC = () => {
  const { testToken } = useParams<{ testToken: string }>();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<TestResult | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (testToken) {
      fetchResult();
    }
  }, [testToken]);

  const fetchResult = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await getResult(testToken!);
      console.log('Test result:', response.data);
      
      // Ensure score_percentage is a number
      const resultData = {
        ...response.data,
        score_percentage: Number(response.data.score_percentage)
      };
      
      setResult(resultData);
    } catch (error: any) {
      console.error('Error fetching result:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load test result';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (order: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(order)) {
        newSet.delete(order);
      } else {
        newSet.add(order);
      }
      return newSet;
    });
  };

  const getTimeTaken = (): string => {
    if (!result) return '';
    
    const start = new Date(result.started_at);
    const end = new Date(result.completed_at);
    const diffMs = end.getTime() - start.getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScoreColor = (percentage: number): string => {
    if (percentage >= 70) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (percentage: number): string => {
    if (percentage >= 70) return 'bg-green-50 border-green-200';
    if (percentage >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) {
    return <Backdrop message="Loading test results..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <ErrorBox errorMessage={error} />
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const scorePercentage = Number(result.score_percentage);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
              scorePercentage >= 70 ? 'bg-green-100' : 
              scorePercentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <Award className={`w-10 h-10 ${getScoreColor(scorePercentage)}`} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Test Completed!</h2>
            <p className="text-gray-600">Here's how {result.applicant_name} performed</p>
          </div>

          {/* Score Display */}
          <div className={`rounded-lg border-2 p-6 mb-6 ${getScoreBgColor(scorePercentage)}`}>
            <div className="text-center">
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(scorePercentage)}`}>
                {scorePercentage.toFixed(0)}%
              </div>
              <p className="text-gray-700 font-medium">
                {result.correct_answers} out of {result.total_questions} correct
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Correct</span>
              </div>
              <p className="text-2xl font-bold text-indigo-600">{result.correct_answers}</p>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Incorrect</span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {result.questions_attempted - result.correct_answers}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Clock className="w-5 h-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Time Taken</span>
              </div>
              <p className="text-2xl font-bold text-gray-600">{getTimeTaken()}</p>
            </div>
          </div>

          {/* Performance Message */}
          <div className={`rounded-lg p-4 ${
            scorePercentage >= 70 ? 'bg-green-50 border border-green-200' :
            scorePercentage >= 50 ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <TrendingUp className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${
                scorePercentage >= 70 ? 'text-green-600' :
                scorePercentage >= 50 ? 'text-yellow-600' :
                'text-red-600'
              }`} />
              <div>
                <h4 className={`font-semibold mb-1 ${
                  scorePercentage >= 70 ? 'text-green-800' :
                  scorePercentage >= 50 ? 'text-yellow-800' :
                  'text-red-800'
                }`}>
                  {scorePercentage >= 70 ? 'Great Performance!' :
                   scorePercentage >= 50 ? 'Good Effort!' :
                   'Keep Practicing!'}
                </h4>
                <p className={`text-sm ${
                  scorePercentage >= 70 ? 'text-green-700' :
                  scorePercentage >= 50 ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {scorePercentage >= 70 
                    ? 'Excellent work! You demonstrated strong critical thinking skills.'
                    : scorePercentage >= 50
                    ? 'You showed decent understanding. Review the questions below to improve.'
                    : 'Consider reviewing the concepts. The HR team will be in touch regarding next steps.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Detailed Results</h2>
          
          <div className="space-y-4">
            {result.test_questions_with_answers.map((q) => (
              <div
                key={q.question_order}
                className={`border-2 rounded-lg overflow-hidden transition-all ${
                  q.is_correct ? 'border-green-200' : 'border-red-200'
                }`}
              >
                <button
                  onClick={() => toggleQuestion(q.question_order)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center flex-1 text-left">
                    {q.is_correct ? (
                      <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="font-semibold text-gray-800">Question {q.question_order}</span>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-1">{q.question_text}</p>
                    </div>
                  </div>
                  {expandedQuestions.has(q.question_order) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                  )}
                </button>

                {expandedQuestions.has(q.question_order) && (
                  <div className="p-4 border-t bg-gray-50">
                    <p className="text-gray-800 mb-4 font-medium">{q.question_text}</p>
                    
                    <div className="space-y-2 mb-4">
                      {['A', 'B', 'C', 'D'].map((option) => {
                        const optionKey = `option_${option.toLowerCase()}` as keyof QuestionResult;
                        const isSelected = q.selected_option === option;
                        const isCorrect = q.correct_option === option;
                        
                        return (
                          <div
                            key={option}
                            className={`p-3 rounded-lg border-2 ${
                              isCorrect && isSelected
                                ? 'bg-green-50 border-green-500'
                                : isCorrect
                                ? 'bg-green-50 border-green-300'
                                : isSelected
                                ? 'bg-red-50 border-red-500'
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-semibold mr-3 text-sm ${
                                isCorrect
                                  ? 'bg-green-600 text-white'
                                  : isSelected
                                  ? 'bg-red-600 text-white'
                                  : 'bg-gray-200 text-gray-700'
                              }`}>
                                {option}
                              </span>
                              <div className="flex-1">
                                <span className="text-gray-800">{q[optionKey] as string}</span>
                                {isCorrect && (
                                  <span className="ml-2 text-xs font-semibold text-green-600">
                                    ✓ Correct Answer
                                  </span>
                                )}
                                {isSelected && !isCorrect && (
                                  <span className="ml-2 text-xs font-semibold text-red-600">
                                    ✗ Your Answer
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResultScreen;